import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, Users, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import PunchLocationMap from '../../../shared/components/ui/PunchLocationMap';
import Modal from '../../../shared/components/ui/Modal';
import {
  getEmployeeDetail,
  updateEmployee,
  addEmployeeDocument,
  getSalaryStructure,
  saveSalaryStructure,
  previewSalaryStructure,
  getEmployeeOnboarding,
  getDailyUpdates
} from '../../services/api/implementation/impl';
import DailyUpdateCard from './components/DailyUpdateCard';

const TABS = [
  { key: 'profile', label: 'Profile' },
  { key: 'onboarding', label: 'Onboarding' },
  { key: 'salary', label: 'Salary' },
  { key: 'documents', label: 'Documents' },
  { key: 'attendance', label: 'Attendance' },
  { key: 'updates', label: 'Daily updates' },
  { key: 'reporting', label: 'Reporting' }
];

const DOC_TYPES = [
  'OFFER_LETTER',
  'CONTRACT',
  'ID_PROOF',
  'ADDRESS_PROOF',
  'EDUCATION',
  'CERTIFICATION',
  'LICENCE',
  'MEDICAL',
  'BANK_PROOF',
  'RELIEVING_LETTER',
  'OTHER'
];

/** Types where an expiry date is worth insisting on. */
const EXPIRING_TYPES = ['CERTIFICATION', 'LICENCE', 'MEDICAL', 'CONTRACT'];

const EmployeeProfile = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/hr/employees' });
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('profile');
  const [docModal, setDocModal] = useState(false);

  const { data: employee, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['employee', id],
    queryFn: () => getEmployeeDetail(id),
    enabled: Boolean(id),
    retry: 1
  });

  if (isFetching && !employee) {
    return (
      <Layout>
        <Header backArrow onBack={onBack} data={{ name: 'Employee' }} />
        <Loader isVisible />
      </Layout>
    );
  }

  if (isError || !employee) {
    return (
      <Layout>
        <Header backArrow onBack={onBack} data={{ name: 'Employee' }} />
        <div className="m-4 rounded-control border border-warning-border bg-warning-bg p-3 text-xs text-warning-fg">
          <p className="font-medium">Could not open this employee.</p>
          <p className="mt-1 text-warning-fg">
            {error?.response?.data?.message ||
              error?.message ||
              'The record is on the list, but the profile did not load.'}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-brand-700 px-3 py-1.5 text-xs font-medium text-white"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => navigate('/builder/hr/employees')}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-700"
            >
              Back to employees
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: employee.name ?? 'Employee' }} />

      <div className="p-4 space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{employee.name}</h2>
              <p className="text-sm text-gray-500">
                {[employee.designation, employee.vertical].filter(Boolean).join(' · ') ||
                  '—'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {employee.email} · {employee.phone_number}
              </p>
            </div>
            <div className="text-right text-sm">
              <div className="text-gray-500 text-xs">Employee code</div>
              <div className="font-medium">{employee.employee_code ?? '—'}</div>
            </div>
          </div>
        </div>

        <div className="flex gap-2 border-b border-gray-200">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === t.key
                  ? 'border-brand-700 text-brand-800'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'profile' && <ProfileTab employee={employee} onSaved={onBack} />}

        {tab === 'salary' && <SalaryTab employee={employee} />}

        {tab === 'documents' && (
          <DocumentsTab
            employee={employee}
            onAdd={() => setDocModal(true)}
          />
        )}

        {tab === 'onboarding' && <OnboardingTab employeeId={employee.id} />}
        {tab === 'attendance' && <AttendanceTab employee={employee} />}
        {tab === 'updates' && <DailyUpdatesTab employee={employee} />}

        {tab === 'reporting' && <ReportingTab employee={employee} />}
      </div>

      {docModal && (
        <DocumentModal
          employeeId={employee.id}
          onClose={() => setDocModal(false)}
          onAdded={() => {
            queryClient.invalidateQueries({ queryKey: ['employee', id] });
            queryClient.invalidateQueries({ queryKey: ['expiringDocuments'] });
            setDocModal(false);
          }}
        />
      )}
    </Layout>
  );
};

const ProfileTab = ({ employee, onSaved }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(() => toForm(employee));

  useEffect(() => setForm(toForm(employee)), [employee]);

  const save = useMutation({
    mutationFn: (payload) => updateEmployee(employee.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee', String(employee.id)] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success('Saved');
      // Close the form like the other detail pages do: back to the list (or
      // wherever returnTo points). Staying on the page read as "nothing happened".
      onSaved?.();
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not save')
  });

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      <Section title="Employment">
        <Grid>
          <Select
            label="Employment type"
            value={form.employment_type}
            onChange={set('employment_type')}
            options={['PERMANENT', 'PROBATION', 'CONTRACT', 'INTERN', 'CONSULTANT']}
          />
          <Select
            label="Status"
            value={form.status}
            onChange={set('status')}
            options={['ACTIVE', 'ON_NOTICE', 'SUSPENDED', 'EXITED']}
          />
          <Input label="Employee code" value={form.employee_code} onChange={set('employee_code')} />
          <Input label="Date of joining" type="date" value={form.date_of_joining} onChange={set('date_of_joining')} />
          <Input label="Confirmation date" type="date" value={form.confirmation_date} onChange={set('confirmation_date')} />
          <Input label="Work location" value={form.work_location} onChange={set('work_location')} />
        </Grid>

        {form.status === 'EXITED' && (
          <div className="mt-3 rounded-control border border-warning-border bg-warning-bg p-3">
            <div className="flex items-center gap-2 text-sm text-warning-fg mb-2">
              <AlertTriangle size={15} />
              An exit date is required before this can be saved.
            </div>
            <Grid>
              <Input label="Date of exit" type="date" value={form.date_of_exit} onChange={set('date_of_exit')} />
              <Input label="Reason" value={form.exit_reason} onChange={set('exit_reason')} />
            </Grid>
          </div>
        )}
      </Section>

      <Section title="Personal">
        <Grid>
          <Input label="Date of birth" type="date" value={form.date_of_birth} onChange={set('date_of_birth')} />
          <Input label="Blood group" value={form.blood_group} onChange={set('blood_group')} />
          <Input label="Emergency contact" value={form.emergency_contact_name} onChange={set('emergency_contact_name')} />
          <Input label="Emergency phone" value={form.emergency_contact_phone} onChange={set('emergency_contact_phone')} />
          <Input label="Relationship" value={form.emergency_contact_relation} onChange={set('emergency_contact_relation')} />
        </Grid>
        <Grid>
          <Input label="Permanent address" value={form.permanent_address} onChange={set('permanent_address')} />
          <Input label="Present address" value={form.present_address} onChange={set('present_address')} />
        </Grid>
      </Section>

      <Section
        title="Payroll identifiers"
        hint="Held for the payroll export only — nothing here computes salary."
      >
        <Grid>
          <Input label="Bank account" value={form.bank_account} onChange={set('bank_account')} />
          <Input label="IFSC" value={form.ifsc} onChange={set('ifsc')} />
          <Input label="UAN" value={form.uan} onChange={set('uan')} />
          <Input label="ESIC number" value={form.esic_no} onChange={set('esic_no')} />
        </Grid>
      </Section>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={save.isPending}
          onClick={() =>
            // Empty boxes go as null. Sent as "" the hidden Date of exit failed
            // every save of an active employee with a date syntax error.
            save.mutate(
              Object.fromEntries(
                Object.entries(form).map(([key, value]) => [key, value === '' ? null : value])
              )
            )
          }
          className="px-5 py-2 rounded-lg bg-brand-700 text-white text-sm font-medium disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

const DocumentsTab = ({ employee, onAdd }) => {
  const documents = employee.documents ?? [];
  const today = new Date();

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <span className="font-medium text-gray-800">Documents</span>
        <button
          type="button"
          onClick={onAdd}
          className="px-3 py-1.5 rounded bg-brand-700 text-white text-xs font-medium"
        >
          Add document
        </button>
      </div>

      {documents.length === 0 ? (
        <div className="py-10 text-center text-gray-500 text-sm">
          <FileText size={22} className="mx-auto mb-2 text-gray-400" />
          No documents held.
        </div>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <Th>Type</Th>
              <Th>Title</Th>
              <Th>Number</Th>
              <Th>Issued</Th>
              <Th>Expires</Th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => {
              const expired = doc.expires_on && new Date(doc.expires_on) < today;
              return (
                <tr key={doc.id} className="border-t border-gray-100">
                  <Td>{titleCase(doc.doc_type)}</Td>
                  <Td>
                    {doc.file?.url ? (
                      <a
                        href={doc.file.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-brand-700 underline"
                      >
                        {doc.title || doc.file.name || 'View'}
                      </a>
                    ) : (
                      doc.title || '—'
                    )}
                  </Td>
                  <Td>{doc.document_number ?? '—'}</Td>
                  <Td>{formatDate(doc.issued_on)}</Td>
                  <Td>
                    {doc.expires_on ? (
                      <span className={expired ? 'text-danger-fg font-medium' : ''}>
                        {formatDate(doc.expires_on)}
                        {expired && ' · expired'}
                      </span>
                    ) : (
                      '—'
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

/**
 * The employee's own attendance, with where they punched.
 *
 * Unlike the labour muster, the coordinate here really is THIS PERSON's — they
 * marked their own day on their own phone — so the map genuinely evidences where
 * they started and ended it. That is why this one shows both punches, the
 * accuracy of each fix, and the boundary they were measured against, where the
 * labour view shows a single advisory pin.
 */
/**
 * Whether this person has produced what the company asks for.
 *
 * Two rules, reported separately because they are chased differently: the
 * document checklist the builder configured, and the identity rule — one of
 * Aadhaar or PAN. The identity rule applies even when no checklist exists.
 *
 * A REJECTED document is shown differently from a MISSING one. "We have it and
 * it was wrong" and "we never got it" need different conversations.
 */
const OnboardingTab = ({ employeeId }) => {
  const { data, isFetching } = useQuery({
    queryKey: ['employeeOnboarding', employeeId],
    queryFn: () => getEmployeeOnboarding(employeeId),
    enabled: Boolean(employeeId)
  });

  if (isFetching) return <Loader isVisible />;
  if (!data) return null;

  const STATE_STYLE = {
    VERIFIED: { label: 'Verified', className: 'bg-success-bg text-success-fg' },
    AWAITING_VERIFICATION: { label: 'Awaiting check', className: 'bg-warning-bg text-warning-fg' },
    REJECTED: { label: 'Rejected', className: 'bg-danger-bg text-danger-fg' },
    MISSING: { label: 'Not provided', className: 'bg-gray-100 text-gray-600' }
  };

  return (
    <div className="space-y-4">
      <div
        className={`rounded-control border p-3 ${
          data.complete ? 'border-success-border bg-success-bg/50' : 'border-warning-border bg-warning-bg/50'
        }`}
      >
        <div className="font-medium text-gray-900">
          {data.complete ? 'Onboarding complete' : 'Onboarding outstanding'}
        </div>
        {!data.complete && (
          <p className="mt-1 text-sm text-gray-700">
            {data.outstanding_count > 0
              ? `${data.outstanding_count} required document(s) still to come.`
              : ''}{' '}
            {(data.identity?.problems ?? []).join(' ')}
          </p>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="text-sm font-semibold text-gray-800">Identity</div>
        <p className="mb-2 text-xs text-gray-500">
          One of Aadhaar or PAN is required — not both. The system checks the
          number is the right shape; it does not check it against any register.
        </p>
        {data.identity?.satisfied ? (
          <span className="rounded bg-success-bg px-2 py-0.5 text-xs font-medium text-success-fg">
            On file via {data.identity.via}
          </span>
        ) : (
          <ul className="list-disc pl-5 text-sm text-warning-fg">
            {(data.identity?.problems ?? ['Identity has not been recorded.']).map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-800">
          Document checklist
        </div>
        {data.checklist.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500">
            No joining documents have been configured, so nothing is being
            chased. Nothing is pre-set — what a company requires on joining is
            its own decision.
          </p>
        ) : (
          <table className="min-w-full text-sm">
            <tbody>
              {data.checklist.map((item) => {
                const style = STATE_STYLE[item.state] ?? STATE_STYLE.MISSING;
                return (
                  <tr key={item.doc_type} className="border-t border-gray-100">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {titleCase(item.doc_type)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {item.is_mandatory ? 'Required' : 'Optional'}
                        {item.requires_verification ? ' · needs checking' : ''}
                        {item.note ? ` · ${item.note}` : ''}
                      </div>
                      {item.rejection_reason && (
                        <div className="mt-0.5 text-xs text-danger-fg">
                          {item.rejection_reason}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${style.className}`}
                      >
                        {style.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const AttendanceTab = ({ employee }) => {
  const rows = employee.recent_attendance ?? [];
  const [open, setOpen] = useState(null);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 font-medium text-gray-800">
        Last 30 days
      </div>
      {rows.length === 0 ? (
        <div className="py-10 text-center text-gray-500 text-sm">
          Nothing recorded yet.
        </div>
      ) : (
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <Th>Date</Th>
              <Th>In</Th>
              <Th>Out</Th>
              <Th>Hours</Th>
              <Th>Status</Th>
              <Th>Where</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <React.Fragment key={row.id}>
                <tr className="border-t border-gray-100">
                  <Td>{formatDate(row.date)}</Td>
                  <Td>{formatTime(row.check_in)}</Td>
                  <Td>
                    {row.check_out ? (
                      formatTime(row.check_out)
                    ) : row.check_in ? (
                      <span className="text-warning-fg">missing</span>
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td>{row.worked_hours ? Number(row.worked_hours) : '—'}</Td>
                  <Td>{titleCase(row.status)}</Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      {row.punch_in_lat ? (
                        <button
                          type="button"
                          onClick={() => setOpen(open === row.id ? null : row.id)}
                          className="text-xs text-brand-700 hover:underline"
                        >
                          {open === row.id ? 'Hide map' : 'Show map'}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">Not recorded</span>
                      )}
                      {/* UNCHECKED is not a failure — it means there was nothing
                          to measure against. Saying "outside" for it would read
                          as a violation nobody committed. */}
                      {row.geofence_result === 'OUTSIDE_ALLOWED' && (
                        <span className="rounded bg-warning-bg px-1.5 py-0.5 text-[10px] font-medium text-warning-fg">
                          Outside
                        </span>
                      )}
                      {row.geofence_result === 'UNCHECKED' && (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
                          Unverified
                        </span>
                      )}
                    </div>
                  </Td>
                </tr>
                {open === row.id && row.punch_in_lat && (
                  <tr className="border-t border-gray-100 bg-gray-50">
                    <td colSpan={6} className="px-4 py-3">
                      <PunchLocationMap
                        height={240}
                        points={[
                          {
                            lat: row.punch_in_lat,
                            lng: row.punch_in_lng,
                            accuracy_m: row.punch_in_accuracy_m,
                            distance_m: row.punch_in_distance_m,
                            label: 'Punched in',
                            time: formatTime(row.check_in),
                            kind: 'IN'
                          },
                          ...(row.punch_out_lat
                            ? [
                                {
                                  lat: row.punch_out_lat,
                                  lng: row.punch_out_lng,
                                  accuracy_m: row.punch_out_accuracy_m,
                                  label: 'Punched out',
                                  time: formatTime(row.check_out),
                                  kind: 'OUT'
                                }
                              ]
                            : [])
                        ]}
                        fence={
                          row.officeLocation
                            ? {
                                lat: row.officeLocation.lat,
                                lng: row.officeLocation.lng,
                                radius_m: row.officeLocation.radius_m,
                                name: row.officeLocation.name
                              }
                            : row.project?.site_lat
                            ? {
                                lat: row.project.site_lat,
                                lng: row.project.site_lng,
                                radius_m: row.project.geofence_radius_m,
                                name: row.project.name
                              }
                            : null
                        }
                        caption="Recorded on this employee's own device when they marked their day."
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const ReportingTab = ({ employee }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
        Reports to
      </div>
      {employee.manager ? (
        <div className="font-medium text-gray-900">{employee.manager.name}</div>
      ) : (
        <p className="text-sm text-gray-500">
          No manager set. Attendance corrections cannot be approved for this
          person until one is.
        </p>
      )}
    </div>

    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500 mb-2">
        <Users size={14} /> Direct reports
      </div>
      {(employee.reports ?? []).length === 0 ? (
        <p className="text-sm text-gray-500">None.</p>
      ) : (
        <ul className="text-sm text-gray-800 space-y-1">
          {employee.reports.map((r) => (
            <li key={r.id}>{r.name}</li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

const DocumentModal = ({ employeeId, onClose, onAdded }) => {
  const [docType, setDocType] = useState('ID_PROOF');
  const [title, setTitle] = useState('');
  const [number, setNumber] = useState('');
  const [issued, setIssued] = useState('');
  const [expires, setExpires] = useState('');

  const add = useMutation({
    mutationFn: (payload) => addEmployeeDocument(employeeId, payload),
    onSuccess: () => {
      toast.success('Document added');
      onAdded();
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not add')
  });

  const expiryMatters = EXPIRING_TYPES.includes(docType);

  const submit = () => {
    if (expiryMatters && !expires) {
      // Not a server rule — the server accepts it — but a certification with no
      // expiry silently drops out of the lapsing-documents report, which is the
      // only reason anybody would notice it before it lapsed.
      toast.error('An expiry date is needed for this document type');
      return;
    }
    add.mutate({
      doc_type: docType,
      title: title || null,
      document_number: number || null,
      issued_on: issued || null,
      expires_on: expires || null
    });
  };

  return (
    <Modal name="Add document" onClose={onClose} width="460px">
      <div className="space-y-3">
        <Field label="Type">
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {DOC_TYPES.map((t) => (
              <option key={t} value={t}>
                {titleCase(t)}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Title">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Document number">
          <input
            type="text"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Issued on">
            <input
              type="date"
              value={issued}
              onChange={(e) => setIssued(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
          <Field label={expiryMatters ? 'Expires on *' : 'Expires on'}>
            <input
              type="date"
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
        </div>

        {expiryMatters && (
          <p className="text-xs text-warning-fg">
            Without an expiry date this will not appear in the lapsing-documents
            report.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded border border-gray-300 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={add.isPending}
            onClick={submit}
            className="px-4 py-2 rounded bg-brand-700 text-white text-sm disabled:opacity-50"
          >
            {add.isPending ? 'Adding…' : 'Add document'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

/**
 * Assign a salary.
 *
 * Payroll skips anyone without a structure, so without this screen a month
 * computes nothing. Revisions are added, never edited — recomputing an old
 * month has to use the structure that applied then.
 */
const SalaryTab = ({ employee }) => {
  const queryClient = useQueryClient();
  const [gross, setGross] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);

  const { data: history, isFetching } = useQuery({
    queryKey: ['salaryStructure', employee.id],
    queryFn: () => getSalaryStructure(employee.id)
  });

  const previewMutation = useMutation({
    mutationFn: (value) => previewSalaryStructure(value),
    onSuccess: (result) => setPreview(result),
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not preview the structure')
  });

  const save = useMutation({
    mutationFn: (payload) => saveSalaryStructure(employee.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salaryStructure', employee.id] });
      setGross('');
      setEffectiveFrom('');
      setReason('');
      setPreview(null);
      toast.success('Salary structure saved');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not save the structure')
  });

  const rows = history ?? [];
  const current = rows[0];

  const submit = () => {
    const monthlyGross = Number(gross);
    if (!Number.isFinite(monthlyGross) || monthlyGross <= 0) {
      toast.error('Enter a monthly gross');
      return;
    }
    if (!effectiveFrom) {
      toast.error('Choose an effective-from date');
      return;
    }
    save.mutate({
      monthly_gross: monthlyGross,
      annual_ctc: monthlyGross * 12,
      effective_from: effectiveFrom,
      revision_reason: reason || undefined
    });
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="text-sm font-semibold text-gray-800 mb-2">Current salary</div>
        {isFetching ? (
          <Loader />
        ) : !current ? (
          <div className="flex items-start gap-2 rounded-control border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning-fg">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              No salary assigned. Payroll skips anyone without a structure — this
              person would be left out of the month entirely.
            </span>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-lg font-semibold text-gray-900">
              ₹{Number(current.monthly_gross).toLocaleString('en-IN')} / month
            </div>
            <div className="text-xs text-gray-500">
              Effective {dateInput(current.effective_from)}
              {current.revision_reason ? ` · ${current.revision_reason}` : ''}
            </div>
            <ComponentTable lines={current.components ?? []} />
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
        <div>
          <div className="text-sm font-semibold text-gray-800">
            {current ? 'Revise salary' : 'Assign salary'}
          </div>
          <p className="text-xs text-gray-500">
            Components come from Payroll Setup. Preview before saving — a revision
            cannot be edited afterwards.
          </p>
        </div>

        <Grid>
          <Field label="Monthly gross">
            <input
              type="number"
              value={gross}
              onChange={(e) => setGross(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Effective from">
            <input
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Reason (optional)">
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
        </Grid>

        {preview?.overflow > 0 && (
          <div className="flex items-start gap-2 rounded-control border border-danger-border bg-danger-bg px-3 py-2 text-xs text-danger-fg">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              Fixed and percentage components exceed this gross by ₹{preview.overflow}.
              Reduce them in Payroll Setup or raise the gross.
            </span>
          </div>
        )}

        {preview?.lines?.length > 0 && <ComponentTable lines={preview.lines} />}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            disabled={!gross || previewMutation.isPending}
            onClick={() => previewMutation.mutate(Number(gross))}
            className="px-4 py-2 rounded border border-gray-300 text-sm disabled:opacity-50"
          >
            {previewMutation.isPending ? 'Working…' : 'Preview'}
          </button>
          <button
            type="button"
            disabled={save.isPending}
            onClick={submit}
            className="px-4 py-2 rounded bg-brand-700 text-white text-sm disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save structure'}
          </button>
        </div>
      </div>

      {rows.length > 1 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm font-semibold text-gray-800 mb-2">Revision history</div>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left font-medium px-3 py-2">Effective from</th>
                <th className="text-left font-medium px-3 py-2">Monthly gross</th>
                <th className="text-left font-medium px-3 py-2">Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(1).map((row) => (
                <tr key={row.id} className="border-t border-gray-100">
                  <td className="px-3 py-2">{dateInput(row.effective_from)}</td>
                  <td className="px-3 py-2">
                    ₹{Number(row.monthly_gross).toLocaleString('en-IN')}
                  </td>
                  <td className="px-3 py-2">{row.revision_reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ComponentTable = ({ lines }) => (
  <table className="min-w-full text-sm border border-gray-200 rounded">
    <thead className="bg-gray-50 text-gray-600">
      <tr>
        <th className="text-left font-medium px-3 py-2">Component</th>
        <th className="text-left font-medium px-3 py-2">Type</th>
        <th className="text-right font-medium px-3 py-2">Amount</th>
      </tr>
    </thead>
    <tbody>
      {lines.map((line) => (
        <tr key={line.code ?? line.name} className="border-t border-gray-100">
          <td className="px-3 py-2">{line.name ?? line.code}</td>
          <td className="px-3 py-2 text-gray-500">{line.type ?? '—'}</td>
          <td className="px-3 py-2 text-right">
            ₹{Number(line.amount ?? 0).toLocaleString('en-IN')}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

/**
 * The employee's daily updates — their record, newest first, the last 90 days.
 * The full company view, filters and export are on HR → Daily updates.
 */
const DailyUpdatesTab = ({ employee }) => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 89);
  const key = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ['dailyUpdates', 'employee', employee.user_id],
    queryFn: () => getDailyUpdates({ user_id: employee.user_id, from: key(from), to: key(to) }),
    enabled: Boolean(employee.user_id)
  });
  const updates = data?.data ?? [];

  if (!employee.user_id) {
    return <p className="text-sm text-gray-500">This employee has no login, so they cannot post updates.</p>;
  }
  if (isFetching && !updates.length) return <Loader />;
  if (isError) {
    return (
      <p className="text-sm text-danger-fg">
        {error?.response?.data?.message || 'Could not load daily updates.'}
      </p>
    );
  }

  return (
    <Section
      title={`Daily updates · ${updates.length} in the last 90 days`}
      hint="Optional — posted from the app at punch-out. Their manager is notified when one arrives."
    >
      {updates.length === 0 ? (
        <p className="text-sm text-gray-500">No updates posted in the last 90 days.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {updates.map((u) => (
            <DailyUpdateCard key={u.id} update={u} showPerson={false} />
          ))}
        </div>
      )}
    </Section>
  );
};

const Section = ({ title, hint, children }) => (
  <div>
    <div className="text-sm font-semibold text-gray-800">{title}</div>
    {hint && <p className="text-xs text-gray-500 mb-2">{hint}</p>}
    <div className="mt-2">{children}</div>
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>
);

const Input = ({ label, type = 'text', value, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input
      type={type}
      value={value ?? ''}
      onChange={onChange}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
    />
  </div>
);

const Select = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <select
      value={value ?? ''}
      onChange={onChange}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
    >
      <option value="">—</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {titleCase(o)}
        </option>
      ))}
    </select>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

/** Trims the joined User fields — the server rejects them and they are not ours to edit. */
function toForm(employee) {
  return {
    employment_type: employee.employment_type ?? '',
    status: employee.status ?? '',
    employee_code: employee.employee_code ?? '',
    date_of_joining: dateInput(employee.date_of_joining),
    confirmation_date: dateInput(employee.confirmation_date),
    date_of_exit: dateInput(employee.date_of_exit),
    exit_reason: employee.exit_reason ?? '',
    date_of_birth: dateInput(employee.date_of_birth),
    blood_group: employee.blood_group ?? '',
    emergency_contact_name: employee.emergency_contact_name ?? '',
    emergency_contact_phone: employee.emergency_contact_phone ?? '',
    emergency_contact_relation: employee.emergency_contact_relation ?? '',
    permanent_address: employee.permanent_address ?? '',
    present_address: employee.present_address ?? '',
    bank_account: employee.bank_account ?? '',
    ifsc: employee.ifsc ?? '',
    uan: employee.uan ?? '',
    esic_no: employee.esic_no ?? '',
    work_location: employee.work_location ?? ''
  };
}

function dateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-${`${d.getDate()}`.padStart(2, '0')}`;
}

function titleCase(value) {
  if (!value) return '—';
  return value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

function formatTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

const Th = ({ children }) => (
  <th className="text-left font-medium px-4 py-3 whitespace-nowrap">{children}</th>
);
const Td = ({ children }) => <td className="px-4 py-3 align-top">{children}</td>;

export default EmployeeProfile;
